// Page « Véhicules » (transport, lot T1a) : la flotte de la société. Liste, ajout, modification, retrait de la
// flotte (un véhicule retiré n'est plus proposé pour une course) et suppression après confirmation (refusée par
// le serveur si le véhicule a déjà servi : on le retire alors de la flotte).
// Lot T2 : classe du véhicule (liste des Réglages : Berline, Berline luxe, Van, Minibus au départ), reprise par la course.
import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fleetName } from "@/lib/ride";
import { useRights } from "@/lib/rights";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Archive, ArchiveRestore, CarFront } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { brand: "", model: "", plate: "", color: "", seats: "", notes: "", vehicle_class: "" };

export default function Fleet() {
  // Lot T5-0 : la secrétaire administrative consulte la flotte sans la modifier (Patron et dispatcheur seulement).
  const canEdit = useRights().dispatch;
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]); // classes de véhicule des Réglages (lot T2)

  const load = useCallback(() => { api.get("/fleet").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings/full").then((r) => setClasses(r.data.vehicle_classes || [])).catch(() => {}); }, []);

  const open = (item) => {
    setForm(item ? { ...EMPTY, ...item, seats: item.seats ?? "" } : EMPTY);
    setDialog({ open: true, item });
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const brandMissing = !form.brand.trim();

  const save = async () => {
    if (brandMissing) return;
    setSaving(true);
    const body = { brand: form.brand, model: form.model, plate: form.plate, color: form.color, seats: form.seats === "" ? "" : Number(form.seats), notes: form.notes, vehicle_class: form.vehicle_class || "" };
    try {
      if (dialog.item) await api.patch(`/fleet/${dialog.item.id}`, body);
      else await api.post("/fleet", body);
      toast.success(dialog.item ? "Véhicule modifié" : "Véhicule ajouté");
      setDialog({ open: false, item: null }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const setActive = async (v, active) => {
    try {
      await api.patch(`/fleet/${v.id}`, { active });
      toast.success(active ? "Véhicule remis dans la flotte" : "Véhicule retiré de la flotte"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const confirmDelete = async () => {
    const v = toDelete; setToDelete(null);
    try { await api.delete(`/fleet/${v.id}`); toast.success("Véhicule supprimé"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const actifs = items.filter((v) => v.active !== false);
  const retires = items.filter((v) => v.active === false);
  const Row = ({ v }) => (
    <div className="p-4 md:p-5 flex flex-wrap items-center gap-4" data-testid={`fleet-row-${v.id}`}>
      <div className="min-w-0 flex-1 max-md:basis-full">
        <div className="flex items-center gap-3 flex-wrap gap-y-1">
          <span className="font-display font-bold">{[v.brand, v.model].filter(Boolean).join(" ")}</span>
          {v.plate && <span className="text-xs font-mono px-2 py-0.5 rounded-md border border-border whitespace-nowrap">{v.plate}</span>}
          {v.active === false && <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground" data-testid={`fleet-retired-${v.id}`}>Retiré de la flotte</span>}
        </div>
        <div className="text-sm text-muted-foreground mt-1 break-words">
          {[v.vehicle_class, v.color, v.seats ? `${v.seats} places` : "", v.notes].filter(Boolean).join(" · ") || "—"}
        </div>
      </div>
      {canEdit && <div className="flex items-center gap-1">
        {v.active === false
          ? <Button size="sm" variant="outline" className="gap-1" onClick={() => setActive(v, true)} data-testid={`fleet-restore-${v.id}`}><ArchiveRestore className="h-3.5 w-3.5" /> Remettre</Button>
          : <Button size="sm" variant="outline" className="gap-1" onClick={() => setActive(v, false)} data-testid={`fleet-retire-${v.id}`}><Archive className="h-3.5 w-3.5" /> Retirer</Button>}
        <Button size="icon" variant="ghost" onClick={() => open(v)} title="Modifier" data-testid={`fleet-edit-${v.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => setToDelete(v)} title="Supprimer" data-testid={`fleet-delete-${v.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Flotte</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Véhicules</h1>
        </div>
        {canEdit && <Button className="gap-2" onClick={() => open(null)} data-testid="fleet-new"><Plus className="h-4 w-4" /> Nouveau véhicule</Button>}
      </div>

      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden divide-y divide-border">
        {actifs.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><CarFront className="h-4 w-4" />{canEdit ? " Aucun véhicule dans la flotte : ajoutez-en un avec « Nouveau véhicule »." : " Aucun véhicule dans la flotte."}</div>
        )}
        {actifs.map((v) => <Row key={v.id} v={v} />)}
      </div>
      {retires.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-overline text-muted-foreground">Retirés de la flotte</div>
          <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border opacity-80">
            {retires.map((v) => <Row key={v.id} v={v} />)}
          </div>
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog.item ? "Modifier le véhicule" : "Nouveau véhicule"}</DialogTitle></DialogHeader>
          <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Marque</Label>
                <Input value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Ex. Mercedes" data-testid="fleet-brand" />
                {brandMissing && <p className="text-xs text-destructive" data-testid="fleet-brand-error">La marque est obligatoire.</p>}
              </div>
              <div className="space-y-2"><Label>Modèle</Label>
                <Input value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="Ex. Classe V" data-testid="fleet-model" /></div>
              <div className="space-y-2"><Label>Immatriculation</Label>
                <Input value={form.plate} onChange={(e) => set("plate", e.target.value)} data-testid="fleet-plate" /></div>
              <div className="space-y-2"><Label>Couleur</Label>
                <Input value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="fleet-color" /></div>
              <div className="space-y-2"><Label>Places passagers</Label>
                <Input type="number" min="1" max="60" value={form.seats} onChange={(e) => set("seats", e.target.value)} data-testid="fleet-seats" /></div>
              <div className="space-y-2 min-w-0"><Label>Classe</Label>
                <Select value={form.vehicle_class || "none"} onValueChange={(v) => set("vehicle_class", v === "none" ? "" : v)}>
                  <SelectTrigger className="min-w-0 [&>span]:truncate [&>span]:block" data-testid="fleet-class"><SelectValue placeholder="Aucune" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {form.vehicle_class && !classes.includes(form.vehicle_class) && <SelectItem value={form.vehicle_class}>{form.vehicle_class}</SelectItem>}
                    {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Ex. sièges enfant, vitres teintées…" data-testid="fleet-notes" /></div>
            <p className="text-xs text-muted-foreground">Marque, couleur et immatriculation servent à la déclaration de course à Monaco.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, item: null })} className="rounded-full">Annuler</Button>
            <Button onClick={save} disabled={brandMissing || saving} className="rounded-full" data-testid="fleet-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{`Supprimer ${toDelete ? fleetName(toDelete) : "ce véhicule"} ?`}</AlertDialogTitle>
            <AlertDialogDescription>Cette action est définitive. Un véhicule qui a déjà servi pour une course ne se supprime pas : retirez-le plutôt de la flotte.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-no">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-yes">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
